import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { TranslationProvider, useTranslation } from '@ibk/shared-translation';
import { Button, Loading } from '@ibk/shared-ui';
import { useBenefitMigration } from '../../hooks/useBenefitMigration';
import { BenefitMigrationTrackingPage } from '../../pages/BenefitMigrationTrackingPage';
import { beneficiosTranslations } from '../../translations';
import { canViewLegacyBenefits, resolveBenefitMigrationEntry } from '../../utils/benefitMigrationPolicy';
import { BENEFIT_MIGRATION_PATH, BENEFIT_MIGRATION_TRACKING_PATH, LEGACY_BENEFITS_HISTORY_PATH, resolveBenefitMigrationPath, } from '../../utils/benefitMigrationRoutes';
import { BenefitMigrationProvider } from './BenefitMigrationProvider';
import { MigrationCompletionTransition } from './MigrationCompletionTransition';
function MigrationLookupError() {
    const migration = useBenefitMigration();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const retry = () => {
        Promise.resolve(migration.refetch()).catch(() => null);
    };
    if (pathname === BENEFIT_MIGRATION_TRACKING_PATH && migration.read)
        return _jsx(BenefitMigrationTrackingPage, {});
    return (_jsxs("div", { role: 'alert', children: [_jsx("p", { children: t('benefits.migration.errors.lookup') }), _jsx(Button, { variant: 'primary', size: 'small', dataTestId: 'migration-lookup-retry', onClick: retry, children: t('benefits.migration.retry') })] }));
}
function DefaultBenefitsRoute({ dedicated, children }) {
    return dedicated ? _jsx(Navigate, { to: '/beneficios', replace: true }) : _jsx(_Fragment, { children: children ?? _jsx(Outlet, {}) });
}
function ReadyGuard({ children }) {
    const migration = useBenefitMigration();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const dedicated = [BENEFIT_MIGRATION_PATH, BENEFIT_MIGRATION_TRACKING_PATH, LEGACY_BENEFITS_HISTORY_PATH].includes(pathname);
    if (!migration.enabled)
        return _jsx(DefaultBenefitsRoute, { dedicated: dedicated, children: children });
    const entry = resolveBenefitMigrationEntry(true, migration.state);
    if (entry === 'loading')
        return _jsx(Loading, { fullScreen: true, message: t('benefits.loading') });
    if (entry === 'error')
        return _jsx(MigrationLookupError, {});
    if (entry === 'existing')
        return _jsx(DefaultBenefitsRoute, { dedicated: dedicated, children: children });
    const destination = resolveBenefitMigrationPath(entry);
    if (entry !== 'emissor')
        return pathname === destination ? _jsx(_Fragment, { children: children ?? _jsx(Outlet, {}) }) : _jsx(Navigate, { to: destination, replace: true });
    if (pathname === LEGACY_BENEFITS_HISTORY_PATH)
        return migration.read && canViewLegacyBenefits(migration.read.snapshot) ? (_jsx(_Fragment, { children: children ?? _jsx(Outlet, {}) })) : (_jsx(Navigate, { to: '/beneficios', replace: true }));
    if (pathname === BENEFIT_MIGRATION_PATH || pathname === BENEFIT_MIGRATION_TRACKING_PATH)
        return _jsx(Navigate, { to: '/beneficios', replace: true });
    return _jsx(_Fragment, { children: children ?? _jsx(Outlet, {}) });
}
function Guard({ children }) {
    const migration = useBenefitMigration();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const completedFlow = !migration.initializing &&
        migration.enabled &&
        migration.state.kind === 'ready' &&
        migration.state.snapshot.phase === 'complete' &&
        (pathname === BENEFIT_MIGRATION_PATH || pathname === BENEFIT_MIGRATION_TRACKING_PATH);
    if (migration.initializing)
        return _jsx(Loading, { fullScreen: true, message: t('benefits.loading') });
    if (completedFlow)
        return _jsx(MigrationCompletionTransition, {}, JSON.stringify(migration.scope));
    return _jsx(ReadyGuard, { children: children });
}
export function BenefitMigrationGuard(props) {
    return (_jsx(TranslationProvider, { translations: beneficiosTranslations, defaultLanguage: 'pt-BR', children: _jsx(Guard, { ...props }) }));
}
export function BenefitMigrationRouteBoundary() {
    return (_jsx(BenefitMigrationProvider, { children: _jsx(BenefitMigrationGuard, {}) }));
}
